/**
 * Khu Làm bài theo bản mẫu: Tổng quan (`S.study`, spec 02), Đề thi ETS và Đề thi thử
 * (`S.tests`, `S.mocks`, spec 03–04), hộp chọn chế độ (`openMode`) và Luyện nghe / Luyện đọc
 * (`S.drill`, spec 05 — giữ hai mục theo QUYET-DINH S1).
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  Attempt,
  Pool,
  PRACTICE_SKILLS,
  RevisionStats,
  type TestSummary,
  type Account,
} from "@ca/shared";
import {
  Button,
  Card,
  CardHead,
  Chip,
  ErrorState,
  Kpi,
  Modal,
  PageHead,
  Pbar,
  Seg,
  Skeleton,
  Switch,
  Tabs,
} from "@ca/ui";
import { api, HttpError } from "./api.ts";
import { uuidv7 } from "./queue.ts";
import { attemptSync } from "./attempt-sync.ts";
import { useRead } from "./revision-ui.tsx";
import { useToday } from "./productivity.tsx";
import { latestScore, useScores, useTests } from "./side-counts.ts";
import { NewBadge } from "./flags.tsx";
import { hiddenPath, featureOn } from "./features.ts";

interface Props {
  account: Account;
  path: string;
  notify: (message: string) => void;
}
type Test = z.infer<typeof TestSummary>;
export function useGo() {
  const navigate = useNavigate();
  return (path: string) => {
    void navigate({ to: "/$", params: { _splat: path.replace(/^\//, "") } });
  };
}
const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
const skillOf = (part: number) => (part <= 4 ? "listening" : "reading");

/* ---------------- Tổng quan ---------------- */
export function Overview({ account }: Props) {
  const go = useGo(),
    today = useToday(account.id),
    stats = useRead(account.id, "/v1/me/stats/summary", RevisionStats),
    ets = useTests(account.id, "ets"),
    mocks = useTests(account.id, "mock"),
    scores = useScores(account.id);
  const inProgress = today.data?.inProgress ?? [];
  const parts = stats.data?.parts ?? [];
  const weakest = (list: typeof parts) =>
    [...list.filter((p) => p.total)].sort((a, b) => a.correct / a.total - b.correct / b.total)[0];
  const listenWeak = weakest(parts.filter((p) => p.part <= 4)),
    readWeak = weakest(parts.filter((p) => p.part >= 5));
  const priority = stats.data?.priorityPart;
  const pp = parts.find((p) => p.part === priority);
  const done = ets.data?.filter((t) => t.completed).length ?? 0,
    doing = ets.data?.filter((t) => t.activeAttemptId).length ?? 0;
  const score = latestScore(scores.data);
  const cards: [string, string, string, string, string, string][] = [
    [
      "/tests",
      "📝",
      "Đề thi ETS",
      `${ets.data?.length ?? 0} đề thật, đủ audio và giải thích từng câu.`,
      done || doing ? `${done} đã làm${doing ? ` · ${doing} đang dở` : ""}` : "Chưa làm bài nào",
      "var(--t-lav)",
    ],
    [
      "/mocks",
      "📄",
      "Đề thi thử",
      "Đề Cá soạn, qua kiểm định trước khi mở.",
      mocks.data?.length ? `${mocks.data.length} đề` : "Đang được soạn",
      "var(--t-blue)",
    ],
    [
      "/practice/listening",
      "🎧",
      "Luyện nghe",
      "Part 1–4, chọn part yếu rồi luyện theo 5 level khó.",
      listenWeak
        ? `Yếu nhất: Part ${listenWeak.part} · ${pct(listenWeak.correct, listenWeak.total)}%`
        : "Chưa làm bài nào",
      "var(--t-sage)",
    ],
    [
      "/practice/reading",
      "📖",
      "Luyện đọc",
      "Part 5–7, mỗi phiên đi trọn cụm bài đọc.",
      readWeak
        ? `Yếu nhất: Part ${readWeak.part} · ${pct(readWeak.correct, readWeak.total)}%`
        : "Chưa làm bài nào",
      "var(--t-yellow)",
    ],
    [
      "/dictation",
      "🎧",
      "Nghe & chép",
      "Chép chính tả từng câu nghe Part 1–4.",
      "Chép từng câu, chấm từng từ",
      "var(--t-pink)",
    ],
    [
      "/errors",
      "📕",
      "Sổ tay lỗi sai",
      "Lỗi gom theo dạng câu & loại bẫy, bấm là trị.",
      stats.data?.groups
        ? `${stats.data.groups} nhóm lỗi · ${stats.data.open} câu sai`
        : "Chưa làm bài nào",
      "var(--t-pink)",
    ],
    [
      "/scores",
      "📈",
      "Bảng điểm",
      "Điểm quy đổi 990 sau mỗi full test.",
      score ? `${score} · mục tiêu ${account.profile.goalScore ?? "—"}` : "Chưa làm bài nào",
      "var(--t-lav)",
    ],
  ];
  const visible = cards.filter(([path]) => !hiddenPath(path));
  return (
    <>
      <PageHead
        space="study"
        title="Làm bài"
        desc="Mọi thứ về đề, câu hỏi, lỗi sai và điểm số. Giờ học chỉ bắt đầu tính khi bạn vào làm một bài; xem các danh sách ở đây thì chưa tính."
      />
      {inProgress.length > 0 && (
        <Card>
          <div className="card-head">
            <h2>Đang dở</h2>
            <span className="faint" style={{ fontSize: 12.5, fontWeight: 700 }}>
              bấm là vào đúng câu đang làm
            </span>
          </div>
          <div className="grid g2">
            {inProgress.map((a) => (
              <div key={a.id} className="resume-card">
                <span className="rc-ic">📝</span>
                <div className="grow">
                  <b>{a.name}</b>
                  <div className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>
                    Đã trả lời {a.answered}/{a.total} câu{a.paused ? " · tạm dừng" : ""}
                  </div>
                  <Pbar pct={pct(a.answered, a.total)} style={{ marginTop: 6 }} />
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    go(`/attempts/${a.id}`);
                  }}
                >
                  Tiếp tục
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}
      {pp && pp.total > 0 && (
        <div className="insight">
          <span className="ii">🧭</span>
          <div className="grow">
            <b>Gợi ý của Cá: ưu tiên Part {pp.part}</b>
            <p>
              Bạn đang đúng {pct(pp.correct, pp.total)}% ở Part {pp.part}, mốc cần cho mục tiêu{" "}
              {account.profile.goalScore ?? "của bạn"} là {pp.target}%. Dồn phần lớn thời gian tuần
              này vào đây là lên điểm nhanh nhất.
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => {
              go(`/practice/${skillOf(pp.part)}/${pp.part}`);
            }}
          >
            Luyện ngay
          </Button>
        </div>
      )}
      <div className="grid g4">
        {visible.map(([to, icon, title, text, status, bg]) => (
          <button
            key={to}
            className="sec-card"
            onClick={() => {
              go(to);
            }}
          >
            <span className="sc-ic" style={{ background: bg }}>
              {icon}
            </span>
            <b>{title}</b>
            <p>{text}</p>
            <span className="sc-status">{status}</span>
          </button>
        ))}
      </div>
    </>
  );
}

/* ---------------- Đề thi ETS / Đề thi thử ---------------- */
// Số đề thi thử dự kiến; khi ít hơn thì hiện dải "đang được soạn" (spec 04.3).
const MOCK_PLANNED = 19;
function TestCard({
  t,
  progress,
  open,
}: {
  t: Test;
  /** Số câu đã làm / tổng của lượt đang dở, lấy từ `today.inProgress` (6 lượt mới nhất). */
  progress: { answered: number; total: number } | undefined;
  open: () => void;
}) {
  const status = t.completed ? (
    <Chip tone="good">
      Đã làm
      {t.lastScore !== null
        ? ` · ${t.lastScore}`
        : t.lastCorrect !== null
          ? ` · ${t.lastCorrect} đúng`
          : ""}
    </Chip>
  ) : t.activeAttemptId ? (
    <Chip tone="warn">Đang dở</Chip>
  ) : (
    <Chip>Chưa làm</Chip>
  );
  const full = t.parts.length === 7;
  return (
    <article className="testcard">
      <div className="tc-top">
        <b>{t.name}</b>
        {status}
      </div>
      <div className="meta">
        {t.questionCount} câu · {t.durationMin} phút
        {!full ? ` · Part ${t.parts.join("–")}` : ""}
      </div>
      <Pbar
        pct={
          t.completed
            ? 100
            : progress?.total
              ? Math.round((progress.answered / progress.total) * 100)
              : 0
        }
      />
      <div className="meta">
        {t.completed
          ? "Bấm để làm lại hoặc xem lỗi"
          : t.activeAttemptId
            ? progress
              ? `Đã làm ${progress.answered}/${progress.total} câu · bấm để tiếp tục`
              : "Đang làm dở · bấm để tiếp tục"
            : "Chưa luyện tập"}
      </div>
      <Button variant="acc" size="sm" onClick={open}>
        ▶ Chọn chế độ làm bài
      </Button>
    </article>
  );
}
export function TestList({ account, path, notify }: Props) {
  const mock = path === "/mocks";
  const [category, setCategory] = useState<"ets" | "rc">(
    featureOn("ets-reading") && new URLSearchParams(window.location.search).get("kind") === "rc"
      ? "rc"
      : "ets",
  );
  const kind = mock ? "mock" : category,
    [set, chooseSet] = useState(""),
    [selected, select] = useState<Test | null>(null);
  const data = useTests(account.id, kind),
    ets = useTests(account.id, "ets"),
    rc = useTests(account.id, "rc"),
    today = useToday(account.id);
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("test");
    if (id && data.data) select(data.data.find((t) => t.id === id) ?? null);
  }, [data.data]);
  const sets = [...new Map((ets.data ?? []).map((t) => [t.setCode, t.setName])).entries()];
  const list = (data.data ?? []).filter((t) => !set || t.setCode === set);
  const tab = category === "rc" ? "rc" : set || "all";
  return (
    <>
      {mock ? (
        <PageHead
          space="study"
          title="Đề thi thử"
          desc="Đề do Cá soạn theo đúng cấu trúc ETS. Chấm điểm và phân tích lỗi ngay khi nộp."
        />
      ) : (
        <PageHead
          space="study"
          title={category === "rc" ? "Bài Reading" : "Đề thi ETS"}
          desc={`${ets.data?.length ?? 0} đề thi thật, đủ audio và giải thích chi tiết từng câu. Chọn chế độ làm full test, làm lẻ từng part, hoặc luyện không tính giờ.`}
        >
          <Tabs
            label="Lọc bộ đề"
            items={[
              { key: "all", label: `Tất cả ${ets.data?.length ?? 0} đề` },
              ...sets.map(([code, name]) => ({
                key: code,
                label: `${name} (${ets.data?.filter((t) => t.setCode === code).length ?? 0})`,
              })),
              ...(featureOn("ets-reading")
                ? [{ key: "rc", label: `Bài Reading (${rc.data?.length ?? 0})` }]
                : []),
            ]}
            active={tab}
            onChange={(k) => {
              if (k === "rc") {
                setCategory("rc");
                chooseSet("");
              } else {
                setCategory("ets");
                chooseSet(k === "all" ? "" : k);
              }
            }}
          />
        </PageHead>
      )}
      {data.isError ? (
        <ErrorState
          message="Chưa tải được đề."
          onRetry={() => {
            void data.refetch();
          }}
        />
      ) : data.isPending ? (
        <Skeleton />
      ) : list.length ? (
        <div className={`grid ${mock ? "g3" : "g4"}`}>
          {list.map((t) => (
            <TestCard
              key={t.id}
              t={t}
              progress={today.data?.inProgress.find((a) => a.id === t.activeAttemptId)}
              open={() => {
                select(t);
              }}
            />
          ))}
        </div>
      ) : (
        <section className="card faint">
          {mock ? "Đề thi thử đang được soạn." : "Chưa có đề được xuất bản."}
        </section>
      )}
      {mock && (data.data?.length ?? 0) < MOCK_PLANNED && (
        <div className="card tight row" style={{ gap: 10 }}>
          <span style={{ fontSize: 20 }}>⏳</span>
          <p>
            <b>Đề {(data.data?.length ?? 0) + 1} trở đi đang được soạn</b> — mỗi đề đều qua kiểm
            định trước khi mở, nên bên mình ra chậm mà chắc.
          </p>
        </div>
      )}
      {selected && (
        <StartModal
          account={account}
          test={selected}
          notify={notify}
          close={() => {
            select(null);
          }}
        />
      )}
    </>
  );
}
type Mode = "exam" | "parts" | "instant";
export function StartModal({
  account,
  test,
  close,
  notify,
}: {
  account: Account;
  test: Test;
  close: () => void;
  notify: Props["notify"];
}) {
  const go = useGo(),
    full = test.parts.length === 7,
    [mode, setMode] = useState<Mode>(full ? "exam" : "instant"),
    [parts, setParts] = useState(test.parts),
    [minutes, setMinutes] = useState(""),
    [pomodoro, setPomodoro] = useState(false),
    [busy, setBusy] = useState(false);
  const pending = useRef<{ id: string; key: string; body: unknown } | null>(null);
  async function start() {
    setBusy(true);
    try {
      pending.current ??= {
        id: uuidv7(),
        key: uuidv7(),
        body: {
          testId: test.id,
          mode: mode === "exam" ? "exam" : "practice",
          parts: mode === "exam" ? test.parts : parts,
          timeLimitSec: mode === "parts" && minutes ? Math.round(Number(minutes) * 60) : null,
          immediate: mode === "instant",
        },
      };
      const p = pending.current,
        a = Attempt.parse(
          await api(`/v1/attempts/${p.id}`, {
            method: "PUT",
            body: p.body,
            key: p.key,
            accountId: account.id,
          }),
        );
      await attemptSync.cache(account.id, a);
      if (pomodoro) window.dispatchEvent(new Event("ca-focus-start"));
      go(`/attempts/${a.id}`);
      close();
    } catch (e) {
      if (e instanceof HttpError) pending.current = null;
      notify(e instanceof Error ? e.message : "Chưa kết nối được.");
    } finally {
      setBusy(false);
    }
  }
  const locked = busy || !!pending.current;
  const opts: [Mode, string, string, string][] = [
    ["exam", "📄", "Thi full test", "Cả 7 part, 1 đồng hồ tổng. Nộp xong mới hiện đáp án."],
    ["parts", "⏱️", "Làm từng part", "Chọn part & tự đặt số phút. Nộp mới hiện đáp án."],
    [
      "instant",
      "🎯",
      "Làm & xem đáp án ngay",
      "Làm câu nào hiện đáp án + giải thích câu đó luôn, không đếm giờ.",
    ],
  ];
  return (
    <Modal
      title="Chế độ làm bài"
      head={
        <div>
          <h2>Chế độ làm bài</h2>
          <p className="muted" style={{ fontSize: 13.5 }}>
            {test.name}
          </p>
        </div>
      }
      onClose={close}
    >
      <div className="stack">
        {test.activeAttemptId && (
          <div className="resume">
            <span>
              <b>⏸ Bạn có bài đang làm dở</b> — chuyển máy khôi phục phần đã đồng bộ.
            </span>
            <div className="row">
              <Button
                variant="acc"
                size="sm"
                onClick={() => {
                  go(`/attempts/${test.activeAttemptId ?? ""}`);
                }}
              >
                ▶ Tiếp tục
              </Button>
              <span className="faint" style={{ fontSize: 12.5, fontWeight: 650 }}>
                hoặc chọn chế độ dưới đây để làm lượt mới (giữ lịch sử bài cũ)
              </span>
            </div>
          </div>
        )}
        <div className="stack" role="radiogroup" aria-label="Cách làm bài">
          {opts.map(([key, icon, title, sub]) => (
            <button
              key={key}
              className="opt"
              role="radio"
              aria-checked={mode === key}
              aria-label={title}
              disabled={locked || (key === "exam" && !full)}
              onClick={() => {
                setMode(key);
              }}
            >
              <span className="o-ic">{icon}</span>
              <span>
                <b>{title}</b>
                <small>{key === "exam" && !full ? "Đề này không đủ 7 part." : sub}</small>
              </span>
            </button>
          ))}
        </div>
        {mode === "parts" && (
          <div className="kpi" style={{ background: "var(--surface-2)", gap: 10 }}>
            <div className="row" style={{ gap: 6 }}>
              {test.parts.map((p) => (
                <button
                  key={p}
                  role="checkbox"
                  aria-checked={parts.includes(p)}
                  aria-label={`Part ${p}`}
                  disabled={locked}
                  className={`chip${parts.includes(p) ? " dark" : ""}`}
                  style={{ height: 32, padding: "0 12px" }}
                  onClick={() => {
                    setParts((prev) =>
                      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p].sort(),
                    );
                  }}
                >
                  Part {p}
                </button>
              ))}
            </div>
            <div className="field">
              <label htmlFor="mode-minutes">Thời gian (phút, để trống nếu không giới hạn)</label>
              <input
                id="mode-minutes"
                className="input"
                aria-label="Thời gian luyện"
                type="number"
                min="1"
                max="240"
                disabled={locked}
                value={minutes}
                onChange={(e) => {
                  setMinutes(e.target.value);
                }}
              />
            </div>
          </div>
        )}
        <div
          className="kpi"
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 12,
            background: "var(--accent-soft)",
          }}
        >
          <span style={{ fontSize: 20 }}>🍅</span>
          <div className="grow">
            <b>
              Bật Pomodoro khi làm <NewBadge />
            </b>
            <div className="k-sub">
              Đồng hồ thu nhỏ ở thanh trên · giờ làm bài tự ghi vào báo cáo
            </div>
          </div>
          <Switch label="Bật Pomodoro khi làm" checked={pomodoro} onChange={setPomodoro} />
        </div>
        <div className="kpi" style={{ background: "var(--surface-2)" }}>
          <span className="k-sub" style={{ fontWeight: 700, color: "var(--ink-2)" }}>
            {mode === "exam"
              ? `Toàn bộ 7 parts — ${test.questionCount} câu — ${test.durationMin} phút`
              : `Part ${(mode === "parts" ? parts : test.parts).join(", ")}`}
          </span>
        </div>
        <Button
          variant="acc"
          size="lg"
          disabled={busy || (mode === "parts" && !parts.length)}
          onClick={() => {
            void start();
          }}
        >
          {busy ? "Đang mở bài…" : pending.current ? "Gửi lại yêu cầu mở bài" : "▶ Bắt đầu"}
        </Button>
        {pending.current && (
          <Button
            variant="line"
            onClick={() => {
              pending.current = null;
              setBusy(false);
            }}
          >
            Đổi cấu hình
          </Button>
        )}
      </div>
    </Modal>
  );
}

/* ---------------- Luyện nghe / Luyện đọc ---------------- */
const LEVELS: [string, string, string][] = [
  ["🌱", "Level 1 — Dễ", "Dễ nhất trong part này — nhìn là ra, từ phổ thông"],
  ["📗", "Level 2 — Cơ bản", "Phải đọc kỹ một chút, có bẫy nhại từ khoá"],
  ["🎯", "Level 3 — Trung bình", "Từ vựng bậc cao hơn hoặc phải suy một bước"],
  ["🔥", "Level 4 — Khó", "Tốn giờ, suy luận nhiều bước, bẫy hiểm"],
  ["👑", "Level 5 — Rất khó", "Nhiều trục khó cùng lúc"],
];
const P7_KEYS = [
  ["7s", "Đoạn đơn"],
  ["7d", "Đoạn đôi"],
  ["7t", "Đoạn ba"],
] as const;
export function Practice({ account, path, notify }: Props) {
  const skill = PRACTICE_SKILLS.find(
      (s) => path === `/practice/${s.key}` || path.startsWith(`/practice/${s.key}/`),
    ),
    go = useGo();
  const part = Number(path.split("/")[3]) || skill?.parts[0] || 1;
  const pools = useQuery({
      queryKey: ["pools", account.id],
      queryFn: async () => z.array(Pool).parse(await api("/v1/drill/pools")),
    }),
    stats = useRead(account.id, "/v1/me/stats/summary", RevisionStats);
  const [busy, setBusy] = useState(false),
    [p7, setP7] = useState<string>("7s"),
    pending = useRef<{ id: string; key: string; body: unknown } | null>(null);
  if (!skill || !skill.parts.includes(part))
    return (
      <ErrorState
        message="Part không thuộc kỹ năng này."
        onRetry={() => {
          go("/study");
        }}
      />
    );
  async function start(poolKey: string, level: number) {
    setBusy(true);
    try {
      pending.current ??= { id: uuidv7(), key: uuidv7(), body: { poolKey, level } };
      const p = pending.current,
        a = Attempt.parse(
          await api(`/v1/drill/sessions/${p.id}`, {
            method: "PUT",
            body: p.body,
            key: p.key,
            accountId: account.id,
          }),
        );
      await attemptSync.cache(account.id, a);
      go(`/attempts/${a.id}`);
    } catch (e) {
      if (e instanceof HttpError) pending.current = null;
      notify(e instanceof Error ? e.message : "Chưa mở được phiên luyện.");
    } finally {
      setBusy(false);
    }
  }
  const poolKey = part === 7 ? p7 : String(part);
  const levelPools = [1, 2, 3, 4, 5].map((l) =>
    pools.data?.find((r) => r.poolKey === poolKey && r.level === l),
  );
  const rows = (stats.data?.parts ?? []).filter((p) => skill.parts.includes(p.part));
  const withData = rows.filter((p) => p.total);
  const weakest = [...withData].sort((a, b) => a.correct / a.total - b.correct / b.total)[0];
  const avg = withData.length
    ? Math.round(withData.reduce((n, p) => n + (p.correct / p.total) * 100, 0) / withData.length)
    : null;
  const avgTarget = rows.length
    ? Math.round(rows.reduce((n, p) => n + p.target, 0) / rows.length)
    : null;
  const below = withData.filter((p) => pct(p.correct, p.total) < p.target);
  const cur = rows.find((p) => p.part === part);
  return (
    <>
      <PageHead
        space="study"
        title={skill.label}
        desc={`Chọn part bạn đang yếu, rồi chọn level hợp với trình hiện tại. Câu được gom từ toàn bộ kho đề ETS & đề thi thử; câu thuộc ${skill.key === "listening" ? "đoạn nghe" : "bài đọc"} luôn đi trọn cụm.`}
      >
        <Tabs
          label="Chọn part"
          items={skill.parts.map((p) => ({ key: String(p), label: `Part ${p}` }))}
          active={String(part)}
          onChange={(k) => {
            go(`/practice/${skill.key}/${k}`);
          }}
        />
      </PageHead>
      <Card>
        <CardHead
          title="🎯 Bạn đang đứng ở đâu"
          sub={
            withData.length
              ? `Mốc lấy theo mục tiêu ${account.profile.goalScore ?? "—"} điểm · ${below.length}/${withData.length} part đang dưới mốc${weakest ? ` · hụt nhiều nhất: P${weakest.part}` : ""}`
              : "Làm Level 1 để Cá biết bạn đang ở đâu"
          }
        >
          <Button
            variant="line"
            size="sm"
            onClick={() => {
              go("/scores");
            }}
          >
            Đổi mục tiêu →
          </Button>
        </CardHead>
        <div className="kpis">
          <Kpi
            style={{ background: "var(--bad-bg)" }}
            label="Yếu nhất"
            value={
              <span style={{ color: "var(--bad)" }}>{weakest ? `P${weakest.part}` : "—"}</span>
            }
            sub={
              weakest
                ? `${pct(weakest.correct, weakest.total)}% · hụt ${Math.max(0, weakest.target - pct(weakest.correct, weakest.total))} điểm %`
                : "chưa có dữ liệu"
            }
          />
          <Kpi
            label="Trung bình"
            value={avg === null ? "—" : `${avg}%`}
            sub={avgTarget === null ? "" : `mốc cần ${avgTarget}%`}
          />
          <Kpi
            label={`Đã luyện Part ${part}`}
            value={cur ? `${pct(cur.poolDone, cur.poolTotal)}%` : "0%"}
            sub={cur ? `${cur.poolDone}/${cur.poolTotal} câu` : "0 câu"}
          />
        </div>
        <div className="grid g2" style={{ marginTop: 16 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 8 }}>
              Độ chính xác từng part
            </div>
            {rows.map((p) => {
              const v = pct(p.correct, p.total),
                tone = !p.total ? "" : v >= p.target ? "good" : v >= p.target - 10 ? "mid" : "bad";
              return (
                <div
                  key={p.part}
                  className="acc-row"
                  data-tip={`P${p.part} · ${v}% (${p.correct}/${p.total} câu)|vạch đen = mốc ${p.target}%`}
                >
                  <span>P{p.part}</span>
                  <span className="acc-track">
                    <i className={tone ? `st-${tone}` : undefined} style={{ width: `${v}%` }} />
                    <span className="mk" style={{ left: `${p.target}%` }} />
                  </span>
                  <span className="av">
                    {p.total ? `${v}%` : "—"}
                    <small>
                      {p.total
                        ? `${p.correct}/${p.total} · ${tone === "bad" ? "✕ hụt nhiều" : tone === "mid" ? "! hụt vừa" : "✓ đạt"}`
                        : "chưa làm"}
                    </small>
                  </span>
                </div>
              );
            })}
          </div>
          <div>
            <div className="eyebrow" style={{ marginBottom: 8 }}>
              Part {part} theo level
            </div>
            {levelPools.map((pool, i) => {
              const total = pool?.total ?? 0,
                doneN = total - (pool?.remaining ?? 0);
              return (
                <div
                  key={i}
                  className="acc-row"
                  style={{ gridTemplateColumns: "44px minmax(0,1fr) 70px" }}
                >
                  <span>
                    {LEVELS[i]?.[0]} L{i + 1}
                  </span>
                  <span className="acc-track">
                    <i className="b1" style={{ width: `${pct(doneN, total)}%` }} />
                  </span>
                  <span className="av">
                    {doneN}/{total}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </Card>
      {part === 7 && (
        <Seg
          label="Dạng bài Part 7"
          options={P7_KEYS.map(([value, label]) => ({ value, label }))}
          value={p7}
          onChange={setP7}
          style={{ alignSelf: "flex-start" }}
        />
      )}
      {pools.isError ? (
        <ErrorState
          message="Chưa tải được kho luyện."
          onRetry={() => {
            void pools.refetch();
          }}
        />
      ) : (
        <div className="grid g3">
          {LEVELS.map(([icon, title, text], i) => {
            const pool = levelPools[i];
            return (
              <article key={title} className="levelcard">
                <div className="lv-top">
                  <span style={{ fontSize: 22 }}>{icon}</span>
                  {pool?.locked ? (
                    <Chip tone="pro">🔒 Pro</Chip>
                  ) : (
                    <Chip>{pool?.total ?? 0} câu</Chip>
                  )}
                </div>
                <b>{title}</b>
                <p>{text}</p>
                <p className="faint" style={{ fontSize: 12, fontWeight: 700 }}>
                  {pool ? `${pool.remaining}/${pool.total} câu mới · ~20 câu/phiên` : "Chưa có câu"}
                </p>
                {pool?.locked ? (
                  <Button
                    variant="soft"
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      // Server trả lỗi Pro kèm hộp mời nâng cấp đúng nội dung bị khoá.
                      void start(poolKey, i + 1);
                    }}
                  >
                    🔒 Mở khoá Pro
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    disabled={busy || !pool || pool.remaining === 0}
                    aria-label={`Luyện Level ${i + 1}`}
                    onClick={() => {
                      void start(poolKey, i + 1);
                    }}
                  >
                    {pool && pool.remaining === 0 ? "Đã làm hết câu mới" : "Luyện ngay →"}
                  </Button>
                )}
              </article>
            );
          })}
        </div>
      )}
      {pending.current && (
        <Button
          disabled={busy}
          onClick={() => {
            void start("", 0);
          }}
        >
          Gửi lại yêu cầu mở phiên
        </Button>
      )}
    </>
  );
}
